"use client";

import {
  Statsig as StatsigReact,
  StatsigContext,
  StatsigProvider,
  StatsigUser,
  useGate,
} from "statsig-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Statsig from "statsig-js";
import { clientStatsigOptions } from "./client";

const STORAGE_KEY = "STATSIG_LOCAL_STORAGE_STABLE_ID";

function setupCrossAuthStableId() {
  let stableId = localStorage.getItem(STORAGE_KEY);

  if (stableId) {
    // Set it as a cookie with longer expiration than auth cookies
    document.cookie = `pu_stable_id=${stableId};path=/;max-age=${365 * 24 * 60 * 60};`;
  } else {
    const match = document.cookie.match(
      new RegExp("(^| )pu_stable_id=([^;]+)")
    );
    stableId = match ? match[2] : null;

    if (stableId) {
      // If found in cookie but not localStorage, save to localStorage
      localStorage.setItem(STORAGE_KEY, stableId);
    }
  }
}

/**
 * Helper function to read a cookie value by name
 * Used for extracting geolocation cookies set by middleware
 */
function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : undefined;
}

function getDeviceType(): string {
  if (typeof window === "undefined") return "server";
  const w = window.innerWidth;
  if (w <= 767) return "mobile";
  if (w <= 1024) return "tablet";
  return "desktop";
}

export type StatsigUserInfo = {
  id?: string;
  createdAt?: string;
} | null;

/**
 * Extra Statsig custom unit IDs (e.g. `{ comedianId }`) merged into the
 * client Statsig user. Gates bucketed on a custom unit type — like the
 * comedian-keyed `newsletter_tab` — can only pass on the client when the
 * user object carries that unit ID, so surfaces that know their entity
 * (the comedian dashboard layout knows its selected comedian) publish it
 * here. `null` clears any previously set IDs.
 */
export type StatsigCustomIds = Record<string, string> | null;

type SetStatsigCustomIds = (customIds: StatsigCustomIds) => void;

const StatsigCustomIdsContext = createContext<SetStatsigCustomIds>(() => {
  // No-op outside the provider: a surface rendered without Statsig (tests,
  // storybook) can still call the setter safely.
});

/**
 * Returns a stable setter that merges custom unit IDs into the client
 * Statsig user (triggering a gate re-evaluation via `Statsig.updateUser`).
 * Call with `null` to clear — callers should clear on unmount so unrelated
 * surfaces don't inherit a stale entity ID.
 */
export function useSetStatsigCustomIds(): SetStatsigCustomIds {
  return useContext(StatsigCustomIdsContext);
}

/**
 * A gate bucketed on a custom unit, answered for `unitId` alone: `false` until
 * Statsig has finished switching to a user that carries
 * `customIDs[unitType] === unitId`, then the gate's value. A surface publishes
 * its unit from an effect, so after a switch of entity (one venue to another)
 * Statsig still holds the previous unit's values until its update lands;
 * comparing units keeps one entity's answer off the other. The held unit is
 * re-read on `userVersion`: `StatsigProvider` (statsig-react 1.36) bumps it in
 * the `.then` of every `Statsig.updateUser` it starts on a user change, and
 * that promise always resolves (a failed update resolves `false`), so the
 * answer follows the switch without a reload. `getCurrentUser()` is read in a
 * memo rather than on every render because Statsig swaps its user at the
 * START of an update, before the new values land.
 */
export function useGateForCustomId(
  gateName: string,
  unitType: string,
  unitId: string | null
): boolean {
  const { value } = useGate(gateName);
  const { userVersion } = useContext(StatsigContext);
  const heldUnitId = useMemo(
    () => StatsigReact.getCurrentUser()?.customIDs?.[unitType],
    // userVersion is the re-read signal, not a value the read uses.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitType, userVersion]
  );
  return unitId !== null && heldUnitId === unitId && value;
}

type StatsigProviderWrapperProps = {
  children: React.ReactNode;
  /**
   * User info from auth provider. Pass null for logged-out users,
   * undefined while loading (will delay Statsig initialization).
   */
  user: StatsigUserInfo | undefined;
};

/**
 * Build the Statsig user object with lightweight enrichment so that
 * we can slice metrics by device, login, subscriber and tenure without
 * extra network calls or heavy dependencies.
 *
 * Includes geolocation (city/state) from cookies set by middleware.
 */
function buildStatsigUser(
  user: StatsigUserInfo,
  customIds: StatsigCustomIds
): StatsigUser {
  const device_type = getDeviceType();
  const is_logged_in = Boolean(user?.id);

  // Extract geolocation from cookies (set by middleware from Vercel headers)
  const city = getCookie("pu_city");
  const state = getCookie("pu_state");

  return {
    userID: user?.id ?? undefined,
    // Custom unit IDs (e.g. comedianId) published by entity-aware surfaces
    // via useSetStatsigCustomIds — required for gates bucketed on a custom
    // unit type to evaluate on the client.
    customIDs: customIds ?? undefined,
    custom: {
      device_type,
      is_logged_in,
      signup_date: user?.createdAt ?? undefined,
      city, // Will be undefined if not in deployed Vercel environment
      state, // Will be undefined if not in deployed Vercel environment
    },
  };
}

export const StatsigProviderWrapper = ({
  children,
  user,
}: StatsigProviderWrapperProps) => {
  const [statsigUser, setStatsigUser] = useState<StatsigUser | null>(null);
  const [customIds, setCustomIdsState] = useState<StatsigCustomIds>(null);
  const statsigClientKey = process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY;
  const initRef = useRef(false);

  if (!statsigClientKey) {
    throw new Error("NEXT_PUBLIC_STATSIG_CLIENT_KEY is not set");
  }

  const setCustomIds = useCallback<SetStatsigCustomIds>((next) => {
    // Value-compare so a caller re-publishing the same IDs (e.g. an effect
    // re-run) doesn't churn Statsig.updateUser.
    setCustomIdsState((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next
    );
  }, []);

  // Update Statsig user when the auth user or published custom IDs change
  useEffect(() => {
    // Don't initialize until we know the auth state (user is not undefined)
    if (user === undefined) return;

    if (typeof window !== "undefined") {
      setupCrossAuthStableId();
    }

    setStatsigUser(buildStatsigUser(user, customIds));
  }, [user, customIds]);

  // Initialize or update Statsig
  useEffect(() => {
    if (typeof window === "undefined" || statsigUser === null) return;

    setupCrossAuthStableId();

    const stableId = localStorage.getItem(STORAGE_KEY);

    const options = {
      ...clientStatsigOptions,
      overrideStableID: stableId || undefined,
    };

    const initializedRef = initRef.current;
    if (!initializedRef) {
      Statsig.initialize(statsigClientKey, statsigUser, options);
      initRef.current = true;
    } else {
      Statsig.updateUser(statsigUser);
    }
  }, [statsigClientKey, statsigUser]);

  return (
    <StatsigCustomIdsContext.Provider value={setCustomIds}>
      {statsigUser ? (
        <StatsigProvider
          sdkKey={statsigClientKey}
          waitForInitialization={true}
          user={statsigUser}
          options={clientStatsigOptions}
          mountKey="stable"
        >
          {children}
        </StatsigProvider>
      ) : null}
    </StatsigCustomIdsContext.Provider>
  );
};
